import * as React from "react";

import styles from "./UploadDropzone.module.css";
import { IconBadge } from "../IconBadge/IconBadge";
import { fileExtVisual } from "../FileRow/FileRow";

export interface UploadDropzoneProps {
  hasFile: boolean;
  fileName?: string;
  fileMeta?: string;
  onPick: (file: File) => void;
  onRemove?: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
  accept?: string;
  hint?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
}

/** Dashed drop area / filled-file row for a single document upload. */
export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  hasFile, fileName, fileMeta, onPick, onRemove, onDownload, isDownloading = false, accept, hint, disabled, invalid, className,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dragCounter = React.useRef(0);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const pick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onPick(file);
    e.target.value = "";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragCounter.current += 1;
    setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragCounter.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onPick(file);
  };

  if (hasFile) {
    const v = fileExtVisual(fileName ?? "");
    return (
      <div className={`${styles.fileRow}${className ? ` ${className}` : ``}`}>
        <IconBadge icon={v.icon} bg={v.bg} color={v.color} size={36} />
        <div
          className={styles.fileInfo}
          onClick={isDownloading ? undefined : onDownload}
          style={onDownload && !isDownloading ? { cursor: "pointer", opacity: isDownloading ? 0.6 : 1 } : undefined}
        >
          <div className={styles.fileName} title={fileName}>{fileName}</div>
          {(fileMeta || isDownloading) && (
            <div className={styles.fileMeta}>
              {isDownloading ? (
                <>
                  <i className="pi pi-spin pi-spinner" style={{ marginRight: 6 }} />
                  Скачивание…
                </>
              ) : fileMeta}
            </div>
          )}
        </div>
        {onRemove && !disabled && (
          <button type="button" title="Удалить файл" className={styles.removeBtn} onClick={onRemove}>
            <i className="fa-regular fa-trash-can" />
          </button>
        )}
        <input ref={inputRef} type="file" accept={accept} hidden onChange={onInput} />
      </div>
    );
  }

  return (
    <div
      className={`${styles.dropzone}${disabled ? ` ${styles.disabled}` : ``}${invalid ? ` ${styles.invalid}` : ``}${isDragOver ? ` ${styles.active}` : ``}${className ? ` ${className}` : ``}`}
      onClick={pick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <i className="fa-solid fa-arrow-up-from-bracket" />
      <span className={styles.dropTitle}>Перетащите файл или нажмите, чтобы выбрать</span>
      {hint && <span className={styles.dropHint}>{hint}</span>}
      <input ref={inputRef} type="file" accept={accept} hidden onChange={onInput} />
    </div>
  );
};
